import { Heart, Users, BookOpen, ArrowRight, ExternalLink } from "lucide-react";
import { Link } from "react-router-dom";

const IMPACT_ITEMS = [
  { amount: "20 EUR", desc: "Financent un kit numerique pour un beneficiaire", icon: "laptop" },
  { amount: "50 EUR", desc: "Permettent l'inscription d'un jeune a un atelier IA", icon: "code" },
  { amount: "100 EUR", desc: "Soutiennent un mois de mentorat pour un apprenant", icon: "users" },
  { amount: "500 EUR", desc: "Financent une session complete de formation", icon: "graduation" },
];

export default function DonatePage() {
  return (
    <div data-testid="donate-page" className="pt-24">
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="font-body text-xs md:text-sm font-medium tracking-[0.2em] uppercase text-blue-400 mb-6">
            Soutenir EDUCPEOPLE
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-slate-50 mb-8 leading-[1.1]">
            Votre don change des vies
          </h1>
          <p className="font-body text-base md:text-lg font-light text-slate-300 max-w-3xl">
            Chaque contribution, quelle que soit sa taille, nous aide a poursuivre notre mission : rendre l'education et les technologies accessibles a tous.
          </p>
        </div>
      </section>

      <div className="section-divider" />

      {/* Why Support */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
          <div>
            <h2 className="font-heading text-3xl md:text-4xl font-light text-slate-50 mb-8">
              Pourquoi soutenir EDUCPEOPLE ?
            </h2>
            <div className="space-y-6">
              {[
                { icon: Heart, title: "Impact direct", desc: "100% de vos dons sont alloues a nos programmes sur le terrain." },
                { icon: Users, title: "Publics vulnerables", desc: "Vous aidez des jeunes, des demandeurs d'emploi et des personnes eloignees du numerique." },
                { icon: BookOpen, title: "Education et innovation", desc: "Vous contribuez a democratiser l'acces a l'IA et aux competences numeriques." },
              ].map((item) => (
                <div key={item.title} className="flex gap-4">
                  <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center shrink-0">
                    <item.icon className="w-5 h-5 text-blue-400" />
                  </div>
                  <div>
                    <h3 className="font-body text-base font-medium text-slate-50 mb-1">{item.title}</h3>
                    <p className="font-body text-sm text-slate-400">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* HelloAsso Embed Placeholder */}
          <div className="bg-slate-900/50 border border-white/10 rounded-3xl p-8 md:p-10">
            <h3 className="font-heading text-2xl font-light text-slate-50 mb-6">Faire un don</h3>
            <p className="font-body text-sm text-slate-400 mb-8">
              Nos dons sont geres via HelloAsso, plateforme securisee pour les associations.
            </p>
            <a
              href="https://www.helloasso.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="helloasso-donate-btn"
              className="inline-flex items-center gap-2 bg-blue-500 hover:bg-blue-400 text-white px-8 py-4 rounded-full font-body font-medium transition-all duration-300 w-full justify-center"
            >
              Faire un don via HelloAsso
              <ExternalLink className="w-4 h-4" />
            </a>
            <p className="font-body text-xs text-slate-500 text-center mt-4">
              66% de votre don est deductible de vos impots
            </p>
          </div>
        </div>
      </section>

      <div className="section-divider" />

      {/* Impact of a donation */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="font-heading text-3xl md:text-4xl font-light text-slate-50 mb-12 text-center">
            L'impact de votre don
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {IMPACT_ITEMS.map((item) => (
              <div
                key={item.amount}
                data-testid={`impact-${item.amount.replace(/\s/g, '-')}`}
                className="bg-slate-900/50 border border-white/10 rounded-2xl p-6 text-center hover:border-blue-500/30 transition-colors"
              >
                <div className="font-body text-2xl font-bold text-blue-400 mb-3">{item.amount}</div>
                <p className="font-body text-sm text-slate-400">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="section-divider" />

      {/* Mecenat & Benevolat */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="bg-slate-900/50 border border-white/10 rounded-3xl p-8 md:p-10">
            <h3 className="font-heading text-2xl font-light text-slate-50 mb-4">Mecenat Entreprise</h3>
            <p className="font-body text-sm text-slate-400 leading-relaxed mb-6">
              Votre entreprise souhaite s'engager pour l'education inclusive ? Nous proposons des partenariats sur mesure : mecenat financier, mecenat de competences, sponsoring d'evenements.
            </p>
            <Link to="/contact" data-testid="mecenat-cta" className="inline-flex items-center gap-2 text-blue-400 font-body text-sm font-medium">
              Nous contacter <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="bg-slate-900/50 border border-white/10 rounded-3xl p-8 md:p-10">
            <h3 className="font-heading text-2xl font-light text-slate-50 mb-4">Benevolat & Mentorat</h3>
            <p className="font-body text-sm text-slate-400 leading-relaxed mb-6">
              Partagez vos competences ! Devenez benevole ou mentor aupres de nos beneficiaires. Que vous soyez professionnel du numerique, enseignant ou etudiant, votre contribution fait la difference.
            </p>
            <Link to="/contact" data-testid="benevolat-cta" className="inline-flex items-center gap-2 text-blue-400 font-body text-sm font-medium">
              Devenir benevole <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
